import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PlannedIncomeService } from '@zaoblako/web/core/services/finance/planned-income.service';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import type { PlannedIncomeBillsQueryInterface } from '@zaoblako/shared/modules/task/dtos/planned-income-bills/planned-income-bills-query.dto';
import { concatMap, from, map, Observable, of, Subscription, toArray } from 'rxjs';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { AsyncPipe, DatePipe, NgIf } from '@angular/common';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { CardModule } from 'primeng/card';
import { StepsModule } from 'primeng/steps';
import { MenuItem } from 'primeng/api';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { FiltersService } from '@zaoblako/web/core/services/filters.service';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { PlannedIncomeBillsGroupInterface } from '@zaoblako/shared/modules/task/dtos/planned-income-bills/planned-income-bills-group.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { ChangeFieldInCardComponent } from '@zaoblako/web/shared/dumbs/change-field-in-card/change-field-in-card.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import type { PlannedIncomeFormInterface } from '@zaoblako/shared/modules/task/dtos/planned-income/planned-income-form.dto';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { PlannedIncomeFindInterface } from '@zaoblako/shared/modules/task/dtos/planned-income/planned-income-find.dto';
import { catchError } from 'rxjs/operators';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { BillFindInterface } from '@zaoblako/shared/modules/document/dtos/bill/bill-find.dto';

@Component({
  selector: 'crm-group-planned-income-form',
  standalone: true,
  imports: [
    AsyncPipe,
    SpinnerComponent,
    CardModule,
    StepsModule,
    DaterangepickerComponent,
    CompanySingleSelectComponent,
    ContragentAutoCompleteComponent,
    ButtonComponent,
    TableComponent,
    CheckboxModule,
    FormsModule,
    ChangeFieldInCardComponent,
    DatePipe,
    DatepickerComponent,
    NgIf
  ],
  templateUrl: './group-planned-income-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupPlannedIncomeFormComponent implements OnInit, OnDestroy {
  @Input() public readonly index: number;

  public activeStep = 0;
  public steps: MenuItem[] = [
    {
      label: 'Поиск счетов'
    },
    {
      label: 'Выбор счетов для создания поступлений'
    }
  ];

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public bills: PlannedIncomeBillsGroupInterface[] = [];

  public billsQueryParams: PlannedIncomeBillsQueryInterface = {
    limit: 200,
    offset: 0
  };

  public selectedCompany: CompanyFindInterface;
  public selectedContragent: ContragentFindInterface;

  public selectItems: string[] = [];

  public readonly columns: CrmTableColumn[] = [
    { class: 'w-3rem' },
    { label: 'Счет' },
    { label: 'Дата' },
    { label: 'Документы' }
  ];

  private formSubscription$: Subscription = new Subscription();

  public get isValidFirstStep(): boolean {
    return Boolean(this.billsQueryParams.from && this.billsQueryParams.to);
  }

  public constructor(
    private readonly plannedIncomeService: PlannedIncomeService,
    private readonly formStoreService: FormStoreService,
    private readonly filtersService: FiltersService,
    private readonly authService: AuthService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);
  }

  public ngOnDestroy() {
    this.formSubscription$.unsubscribe();
  }

  public setDateFilters(dates: [Date, Date]) {
    [this.billsQueryParams.from, this.billsQueryParams.to] = this.filtersService.formatISOPeriod(dates);
  }

  public changeStep() {
    if (!this.activeStep) {
      this.formStoreService.setLoading(true, this.index);

      if (this.selectedCompany) {
        this.billsQueryParams.companyIds = [this.selectedCompany.id];
      } else {
        this.billsQueryParams.companyIds = [];
      }

      if (this.selectedContragent) {
        this.billsQueryParams.contragentIds = [this.selectedContragent.id];
      } else {
        this.billsQueryParams.contragentIds = [];
      }

      this.loadBills();
    }
  }

  public create() {
    this.formStoreService.setLoading(true, this.index);

    const selectedBills = this.bills.filter((bill) => this.selectItems.includes(bill.erp1cNumber));
    let payloadBills: { data: PlannedIncomeBillsGroupInterface; bill: BillFindInterface }[] = [];
    selectedBills.forEach((data) => {
      data.bills.forEach((bill) => {
        payloadBills.push({
          data,
          bill
        });
      });
    });

    from(payloadBills)
      .pipe(
        concatMap((item) => {
          const body: PlannedIncomeFormInterface = {
            date: item.data.date,
            company: item.bill?.company,
            amount: item.bill.amount,
            responsible: this.authService.currentUser
          };

          if (item.bill.contragent) {
            body.contragent = item.bill.contragent;
          }

          if (item.bill.filial) {
            body.filial = item.bill.filial;
          }

          if (item.bill.direction) {
            body.direction = item.bill.direction;
          }

          if (item.bill.unit) {
            body.unit = item.bill.unit;
          }

          if (item.bill.deal) {
            body.deal = item.bill.deal;
          }

          return this.plannedIncomeService.create<PlannedIncomeFindInterface>(body);
        }),
        toArray(),
        map((results) => {
          const successResult = results.filter((res) => res);
          if (successResult?.length === selectedBills?.length) {
            this.errorService.addSuccessMessage('Планируемые поступления были созданы');
            this.formStoreService.saveNext(successResult, this.index);
          } else if (successResult?.length) {
            this.errorService.addSuccessMessage('Планируемые поступления были частично созданы');
            this.formStoreService.saveNext(successResult, this.index);
          } else {
            this.formStoreService.setLoading(false, this.index);
          }
        }),
        catchError((error) => {
          this.errorService.addCustomError(error);
          return of(null);
        })
      )
      .subscribe();
  }

  public toItem(data: any): PlannedIncomeBillsGroupInterface {
    return data as PlannedIncomeBillsGroupInterface;
  }

  public selectItem(item: PlannedIncomeBillsGroupInterface) {
    if (this.selectItems.includes(item.erp1cNumber)) {
      this.selectItems = this.selectItems.filter((i) => i !== item.erp1cNumber);
    } else {
      this.selectItems = this.selectItems.concat(item.erp1cNumber);
    }
  }

  public editDocument(code: string) {
    const result = this.formStoreService.editForm(TypeFormEnum.DOCUMENT, code as any, {
      headerText: `Редактирование документа №${code}`,
      isShowShare: true
    });

    this.formSubscription$.add(result.onSave.subscribe(() => this.loadBills()));
    this.formSubscription$.add(result.onDelete.subscribe(() => this.loadBills()));
  }

  private loadBills() {
    if (!this.formStoreService.getState.forms[this.index]?.loading) {
      this.formStoreService.setLoading(true, this.index);
    }

    this.plannedIncomeService.billsToCreate(this.billsQueryParams).subscribe({
      next: (data) => {
        this.bills = data.items;
        if (this.activeStep !== 1) {
          this.activeStep = 1;
        }
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }
}
